import { useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MapPin, Star } from "lucide-react";
import { IMG, RESTAURANT } from "../data/site";
import { MaskedLine } from "./Motion";

const Hero = () => {
  const ref = useRef(null);
  const [imgSrc, setImgSrc] = useState(IMG.heroGenerated);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const fade = useTransform(scrollYProgress, [0, 0.85], [1, 0]);

  return (
    <section id="home" ref={ref} data-testid="hero-section" className="relative min-h-[100svh] overflow-hidden">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img
          src={imgSrc}
          onError={() => setImgSrc(IMG.heroFallback)}
          alt="Chef preparing a crispy golden dosa on a hot tawa with steam rising"
          className="h-full w-full object-cover"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-b from-ink/80 via-ink/35 to-ink" />
      <div className="absolute inset-0 bg-gradient-to-r from-ink/70 via-transparent to-transparent" />

      <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto flex h-full min-h-[100svh] max-w-7xl flex-col justify-end px-5 pb-28 pt-28 md:px-10 md:pb-32">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.25, duration: 1 }}
          className="mb-6 flex items-center gap-3 text-[11px] uppercase tracking-[0.4em] text-gold"
          data-testid="hero-kicker"
        >
          <span className="h-px w-12 bg-gold" /> Edison, New Jersey · Est. with Love
        </motion.p>

        <h1 className="font-serif text-[13vw] font-medium leading-[0.98] tracking-tight sm:text-6xl md:text-7xl lg:text-[5.4rem]">
          <MaskedLine delay={0.35}>Experience the</MaskedLine>
          <MaskedLine delay={0.5}>
            <em className="italic text-gold">Authentic Taste</em>
          </MaskedLine>
          <MaskedLine delay={0.65}>of South India</MaskedLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.05, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="mt-7 max-w-xl text-base leading-relaxed text-white/70 md:text-lg"
          data-testid="hero-subheading"
        >
          Freshly prepared dosas, biryanis, idlis, Indo-Chinese favorites, chaats —
          and catering for every celebration.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.25, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <a href="/order" data-testid="order-online-button" className="btn-gold rounded-full px-8 py-4 text-[13px] font-semibold uppercase tracking-[0.2em]">
            Order Online
          </a>
          <a href="#menu" data-testid="view-menu-button" className="btn-ghost rounded-full px-8 py-4 text-[13px] uppercase tracking-[0.2em] text-white">
            View Menu
          </a>
          <a href="#catering" data-testid="book-catering-button" className="group rounded-full px-2 py-4 text-[13px] uppercase tracking-[0.2em] text-gold">
            Book Catering
            <span className="ml-2 inline-block transition-transform duration-300 group-hover:translate-x-1.5">→</span>
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.6, duration: 1 }}
          className="mt-12 flex flex-wrap items-center gap-x-8 gap-y-3 text-[12px] uppercase tracking-[0.18em] text-white/50"
        >
          <span className="flex items-center gap-2" data-testid="hero-rating">
            <Star size={13} className="fill-gold text-gold" /> 4.8 · 900+ Google Reviews
          </span>
          <span className="flex items-center gap-2" data-testid="hero-address">
            <MapPin size={13} className="text-gold" /> {RESTAURANT.address1}, {RESTAURANT.address2}
          </span>
        </motion.div>
      </motion.div>
    </section>
  );
};

export default Hero;
